import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, ButtonGroup, Input } from 'reactstrap';

import { useAppSelector } from 'hooks';

import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse } from '~components';
import Button from '~components/Button';
import { TableView } from '~components/table-view';
import { InputProps } from '~components/form-fields';

import { PAGE_SIZE } from '~constants/common';

import { useGetBudgetsPaginatedQuery, useLazyGetBudgetsQuery } from '~support-at-home/actions/budgets';
import { selectClientBudgetStatuses } from '~support-at-home/selectors/clientBudgetStatuses';
import { bulkSahAssignColumns } from '~support-at-home/forms/budgets/bulkSahAssignList';

type SahLinkedFilter = 'all' | 'unlinked' | 'linked';

interface ComponentProps extends InputProps {
  value: string[];
  onChange?: (name: string, value: any) => void;
}

const SelectBudgets: React.FC<ComponentProps> = ({ value: initialIds = [], onChange }) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(Array.isArray(initialIds) ? initialIds : []);
  const [sahLinked, setSahLinked] = useState<SahLinkedFilter>('all');
  const [clientNameInput, setClientNameInput] = useState('');
  const [clientNameDebounced, setClientNameDebounced] = useState('');
  const [statusId, setStatusId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmSelectAll, setConfirmSelectAll] = useState(false);

  const budgetStatuses = useAppSelector(selectClientBudgetStatuses);

  useEffect(() => {
    const timer = setTimeout(() => setClientNameDebounced(clientNameInput), 300);
    return () => clearTimeout(timer);
  }, [clientNameInput]);

  // Reset page and dismiss the select-all prompt when filters change.
  useEffect(() => {
    setCurrentPage(1);
    setConfirmSelectAll(false);
  }, [sahLinked, clientNameDebounced, statusId]);

  const filterParams: Record<string, any> = {};
  if (sahLinked !== 'all') filterParams.sah_linked = sahLinked;
  if (clientNameDebounced) filterParams['client.full_name'] = { like: `%${clientNameDebounced}%` };
  if (statusId) filterParams['status.id'] = [statusId];

  const { data: paginatedData, isFetching, error } = useGetBudgetsPaginatedQuery({
    ...filterParams,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
  });

  const [triggerGetAll, { isFetching: isSelectingAll }] = useLazyGetBudgetsQuery();

  const handleSelectionChange = useCallback(
    (ids: (string | number)[]) => {
      const strIds = ids.map(String);
      setSelectedIds(strIds);
      onChange?.('budget_ids', strIds);
    },
    [onChange],
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleConfirmSelectAll = async () => {
    try {
      const allBudgets = await triggerGetAll(filterParams).unwrap();
      const allIds = allBudgets.map((b: any) => String(b.id));
      setSelectedIds(allIds);
      onChange?.('budget_ids', allIds);
    } catch {
      // Select-all fetch failed — keep the current page selection and close the prompt.
      // (The paginated table surfaces query errors separately; this avoids an unhandled rejection.)
    } finally {
      setConfirmSelectAll(false);
    }
  };

  const total = paginatedData?.total ?? 0;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <Input
          type="text"
          bsSize="sm"
          placeholder="Search client name..."
          value={clientNameInput}
          onChange={(e) => setClientNameInput(e.target.value)}
          className="bulk-sah-assign-client-name-input"
          aria-label="Search by client name"
        />

        <Input
          type="select"
          bsSize="sm"
          value={statusId}
          onChange={(e) => setStatusId(e.target.value)}
          className="bulk-sah-assign-status-select"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {budgetStatuses.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Input>

        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">SAH Link:</span>
          <ButtonGroup size="sm">
            <Button
              size="sm"
              color={sahLinked === 'all' ? 'primary' : 'outline-secondary'}
              onClick={() => setSahLinked('all')}
            >
              All
            </Button>
            <Button
              size="sm"
              color={sahLinked === 'unlinked' ? 'primary' : 'outline-secondary'}
              onClick={() => setSahLinked('unlinked')}
            >
              Unlinked
            </Button>
            <Button
              size="sm"
              color={sahLinked === 'linked' ? 'primary' : 'outline-secondary'}
              onClick={() => setSahLinked('linked')}
            >
              Linked
            </Button>
          </ButtonGroup>
        </div>

        <Badge color="primary" pill>
          {selectedIds.length} selected
        </Badge>
      </div>

      {total > PAGE_SIZE && !confirmSelectAll && (
        <div className="mb-2 small">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setConfirmSelectAll(true)}>
            Select all {total} matching
          </button>
        </div>
      )}

      {confirmSelectAll && (
        <Alert color="warning" className="mb-2 d-flex align-items-center gap-3 py-2">
          <span className="small">
            Select all <strong>{total}</strong> budgets matching current filters?
          </span>
          <Button size="sm" color="warning" disabled={isSelectingAll} onClick={handleConfirmSelectAll}>
            {isSelectingAll ? 'Loading…' : `Yes, select all ${total}`}
          </Button>
          <Button size="sm" color="secondary" disabled={isSelectingAll} onClick={() => setConfirmSelectAll(false)}>
            Cancel
          </Button>
        </Alert>
      )}

      {error && <ErrorResponse message={getErrorMessage(error)} />}

      <TableView
        id="bulk-sah-assign-select-budgets-table"
        columns={bulkSahAssignColumns}
        data={paginatedData?.rows ?? []}
        isLoading={isFetching}
        canSelect
        selectedIds={selectedIds}
        onRowSelectionChange={handleSelectionChange}
        readonly
        striped
        emptyMessage="No budgets found"
        allowPagination
        paginationType="pages"
        showCount
        currentPage={currentPage}
        pageSize={PAGE_SIZE}
        totalCount={total}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default SelectBudgets;
